import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './DungeonMiningMinigame.module.css';
import { ItemData } from '../data/ItemData';
import { MonsterData } from '../data/monsterData';

const GAME_PHASES = {
  IDLE: 'idle',
  FLOOR_INTRO: 'floor_intro',
  EXPLORING: 'exploring',
  COMBAT: 'combat',
  LADDER_FOUND: 'ladder_found',
  GAME_OVER: 'game_over',
  TREASURE_ROOM: 'treasure_room'
};

const FLOOR_THEMES = {
  BROWN_EARTH: { id: 'brown_earth', name: 'Brown Earth', maxFloor: 39, bgColor: '#8B4513', rockColor: '#A0522D' },
  FROZEN_EARTH: { id: 'frozen_earth', name: 'Frozen Earth', maxFloor: 79, bgColor: '#4682B4', rockColor: '#6495ED' },
  LAVA_VOLCANIC: { id: 'lava_volcanic', name: 'Lava Caverns', maxFloor: 120, bgColor: '#B22222', rockColor: '#DC143C' }
};

const DungeonMiningMinigame = ({ playerData, onGameEnd, onUpdatePlayerData }) => {
  // Core game state
  const [phase, setPhase] = useState(GAME_PHASES.IDLE);
  const [currentFloor, setCurrentFloor] = useState(1);
  const [floorGrid, setFloorGrid] = useState([]);
  const [playerPosition, setPlayerPosition] = useState({ x: 0, y: 0 });
  const [playerHealth, setPlayerHealth] = useState(100);
  const [playerEnergy, setPlayerEnergy] = useState(100);
  const [message, setMessage] = useState('');
  
  // Floor generation state
  const [ladderFound, setLadderFound] = useState(false);
  const [ladderPosition, setLadderPosition] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(FLOOR_THEMES.BROWN_EARTH);
  const [unlockedElevators, setUnlockedElevators] = useState([1]);
  
  // Combat state
  const [currentMonster, setCurrentMonster] = useState(null);
  const [monsterHealth, setMonsterHealth] = useState(0);
  const [combatLog, setCombatLog] = useState([]);
  
  // Inventory and rewards
  const [floorInventory, setFloorInventory] = useState({});
  const [totalRewards, setTotalRewards] = useState({});
  
  // Game settings
  const gameSettings = {
    gridSize: 12,
    maxHealth: 100,
    maxEnergy: 100,
    ladderChance: 0.15, // 15% chance per rock broken
    monsterLadderChance: 0.25, // 25% chance when monster defeated
    energyPerMining: 5,
    healthPerHit: 10,
    elvatorInterval: 5,
    treasureInterval: 10
  };

  // Get current theme based on floor
  const getCurrentTheme = useCallback((floor) => {
    if (floor <= 39) return FLOOR_THEMES.BROWN_EARTH;
    if (floor <= 79) return FLOOR_THEMES.FROZEN_EARTH;
    return FLOOR_THEMES.LAVA_VOLCANIC;
  }, []);

  // Generate floor layout
  const generateFloorLayout = useCallback((floor) => {
    const grid = [];
    const size = gameSettings.gridSize;
    
    // Initialize empty grid
    for (let y = 0; y < size; y++) {
      grid[y] = [];
      for (let x = 0; x < size; x++) {
        grid[y][x] = { type: 'empty', discovered: false };
      }
    }
    
    // Add resource nodes (60-80% coverage) - all appear as rocks initially
    const nodeDensity = 0.6 + (Math.random() * 0.2);
    const numNodes = Math.floor(size * size * nodeDensity);
    
    for (let i = 0; i < numNodes; i++) {
      const x = Math.floor(Math.random() * size);
      const y = Math.floor(Math.random() * size);
      
      if (grid[y][x].type === 'empty') {
        // Determine what this node actually contains
        const rand = Math.random();
        let actualContent;
        
        if (rand < 0.15) { // 15% chance for monster
          actualContent = {
            contentType: 'monster',
            monsterType: getRandomMonster(floor)
          };
        } else { // 85% chance for ore/resources
          actualContent = {
            contentType: 'ore',
            oreType: getRandomOre(floor)
          };
        }
        
        // All nodes appear as rocks visually until mined
        grid[y][x] = {
          type: 'rock',
          discovered: false,
          actualContent: actualContent,
          id: `node_${x}_${y}_${Date.now()}`
        };
      }
    }
    
    // Find safe starting position
    let startX = Math.floor(size / 2);
    let startY = Math.floor(size / 2);
    
    // Clear area around starting position
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const newX = startX + dx;
        const newY = startY + dy;
        if (newX >= 0 && newX < size && newY >= 0 && newY < size) {
          grid[newY][newX] = { type: 'empty', discovered: true };
        }
      }
    }
    
    return { grid, startX, startY };
  }, []);

  // Get random ore based on floor depth
  const getRandomOre = useCallback((floor) => {
    const ores = [];
    
    // Stone is always available
    ores.push({ type: 'stone', weight: 40 });
    
    // Add ores based on floor depth
    if (floor >= 2) ores.push({ type: 'copperOre', weight: 30 });
    if (floor >= 40) ores.push({ type: 'ironOre', weight: 20 });
    if (floor >= 80) ores.push({ type: 'goldOre', weight: 15 });
    
    // Add gems with increasing rarity
    if (floor >= 10) ores.push({ type: 'gemShards', weight: 5 });
    if (floor >= 30) ores.push({ type: 'emeraldGem', weight: 3 });
    if (floor >= 50) ores.push({ type: 'rubyGem', weight: 2 });
    if (floor >= 70) ores.push({ type: 'diamondGem', weight: 1 });
    
    // Weighted random selection
    const totalWeight = ores.reduce((sum, ore) => sum + ore.weight, 0);
    const randomValue = Math.random() * totalWeight;
    
    let cumulativeWeight = 0;
    for (const ore of ores) {
      cumulativeWeight += ore.weight;
      if (randomValue <= cumulativeWeight) {
        return ore.type;
      }
    }
    
    return 'stone';
  }, []);

  // Get random monster based on floor depth
  const getRandomMonster = useCallback((floor) => {
    const monsters = [];
    
    // Add monsters based on floor and tier
    if (floor <= 20) {
      monsters.push('slime', 'forestBat');
    } else if (floor <= 40) {
      monsters.push('slime', 'forestBat', 'wildBoar');
    } else if (floor <= 60) {
      monsters.push('wildBoar', 'goblin', 'caveBeetle');
    } else if (floor <= 80) {
      monsters.push('goblin', 'caveBeetle', 'giantSpider');
    } else {
      monsters.push('giantSpider', 'hobgoblin', 'spectre');
    }
    
    return monsters[Math.floor(Math.random() * monsters.length)];
  }, []);

  // Start new floor
  const startNewFloor = useCallback((floor) => {
    const theme = getCurrentTheme(floor);
    setCurrentTheme(theme);
    setCurrentFloor(floor);
    
    // Check if this is a treasure floor
    if (floor % gameSettings.treasureInterval === 0) {
      setPhase(GAME_PHASES.TREASURE_ROOM);
      setMessage(`🏆 Treasure Floor ${floor}! Claim your rewards!`);
      return;
    }
    
    // Generate new floor
    const { grid, startX, startY } = generateFloorLayout(floor);
    setFloorGrid(grid);
    setPlayerPosition({ x: startX, y: startY });
    setLadderFound(false);
    setLadderPosition(null);
    
    // Floor intro
    setPhase(GAME_PHASES.FLOOR_INTRO);
    setMessage(`${theme.name} - Floor ${floor}`);
    
    // Add elevator checkpoint
    if (floor % gameSettings.elvatorInterval === 0) {
      setUnlockedElevators(prev => [...prev, floor]);
    }
    
    setTimeout(() => {
      setPhase(GAME_PHASES.EXPLORING);
      setMessage('Explore the floor! Break rocks to find ore and the ladder to the next level.');
    }, 2000);
  }, [getCurrentTheme, generateFloorLayout]);

  // Initialize game
  const initializeGame = useCallback(() => {
    setPlayerHealth(gameSettings.maxHealth);
    setPlayerEnergy(gameSettings.maxEnergy);
    setFloorInventory({});
    setTotalRewards({});
    setUnlockedElevators([1]);
    startNewFloor(1);
  }, [startNewFloor]);

  // Mine a rock
  const mineRock = useCallback((x, y) => {
    if (playerEnergy < gameSettings.energyPerMining) {
      setMessage('Not enough energy to mine!');
      return;
    }
    
    const rock = floorGrid[y][x];
    if (!rock || rock.type !== 'rock') return;
    
    // Consume energy
    const newEnergy = Math.max(0, playerEnergy - gameSettings.energyPerMining);
    setPlayerEnergy(newEnergy);
    
    // Check if player passed out from energy depletion
    if (newEnergy <= 0) {
      setPhase(GAME_PHASES.GAME_OVER);
      setMessage('You collapsed from exhaustion! You wake up back home with your items.');
              setTimeout(() => {
          const finalRewards = { ...totalRewards };
          Object.entries(floorInventory).forEach(([item, quantity]) => {
            finalRewards[item] = (finalRewards[item] || 0) + quantity;
          });
          
          onGameEnd({
            outcome: 'passed_out',
            rewards: finalRewards,
            tripHaulItems: finalRewards, // Add items to trip haul
            floorsReached: currentFloor,
            finalHealth: playerHealth,
            finalEnergy: 0,
            reason: 'Energy depleted'
          });
        }, 2000);
      return;
    }
    
    // Check what was actually hidden in this rock
    const actualContent = rock.actualContent;
    
    if (actualContent.contentType === 'monster') {
      // It was a monster! Start combat
      const monsterData = MonsterData[actualContent.monsterType];
      if (monsterData) {
        setCurrentMonster({...monsterData, x, y});
        setMonsterHealth(monsterData.stats.health);
        setPhase(GAME_PHASES.COMBAT);
        setCombatLog([`You broke the rock and a ${monsterData.name} emerged!`]);
        setMessage(`Combat! You're fighting a ${monsterData.name}!`);
        return;
      }
    } else if (actualContent.contentType === 'ore') {
      // It was ore/resources
      const oreType = actualContent.oreType;
      setFloorInventory(prev => ({
        ...prev,
        [oreType]: (prev[oreType] || 0) + 1
      }));
      
      // Check for ladder
      if (!ladderFound && Math.random() < gameSettings.ladderChance) {
        setLadderFound(true);
        setLadderPosition({ x, y });
        setMessage(`💎 You found ${ItemData[oreType]?.name || oreType} and discovered a ladder!`);
      } else {
        setMessage(`💎 You found ${ItemData[oreType]?.name || oreType}!`);
      }
    }
    
    // Remove rock from grid and move player to that position
    const newGrid = [...floorGrid];
    newGrid[y][x] = { type: 'empty', discovered: true };
    setFloorGrid(newGrid);
    setPlayerPosition({ x, y });
    
  }, [playerEnergy, floorGrid, ladderFound]);



  // Attack in combat
  const attackMonster = useCallback(() => {
    if (phase !== GAME_PHASES.COMBAT || !currentMonster) return;
    
    const damage = Math.floor(Math.random() * 10) + 5; // 5-15 damage
    const newMonsterHealth = Math.max(0, monsterHealth - damage);
    setMonsterHealth(newMonsterHealth);
    
    let newCombatLog = [...combatLog, `You deal ${damage} damage!`];
    
    if (newMonsterHealth <= 0) {
      // Monster defeated
      newCombatLog.push(`${currentMonster.name} defeated!`);
      
      // Add loot
      const lootTable = currentMonster.lootTable || {};
      Object.entries(lootTable).forEach(([item, data]) => {
        if (Math.random() < data.chance) {
          const quantity = Array.isArray(data.quantity) 
            ? Math.floor(Math.random() * (data.quantity[1] - data.quantity[0] + 1)) + data.quantity[0]
            : data.quantity;
          
          setFloorInventory(prev => ({
            ...prev,
            [item]: (prev[item] || 0) + quantity
          }));
          
          newCombatLog.push(`Found ${quantity} ${ItemData[item]?.name || item}!`);
        }
      });
      
      // Check for ladder
      if (!ladderFound && Math.random() < gameSettings.monsterLadderChance) {
        setLadderFound(true);
        setLadderPosition({ x: monsterX, y: monsterY });
        newCombatLog.push('The monster was guarding a ladder!');
      }
      
      // Remove monster from grid and move player to that position
      const newGrid = [...floorGrid];
      const [monsterX, monsterY] = [currentMonster.x, currentMonster.y];
      newGrid[monsterY][monsterX] = { type: 'empty', discovered: true };
      setFloorGrid(newGrid);
      setPlayerPosition({ x: monsterX, y: monsterY });
      
      setPhase(GAME_PHASES.EXPLORING);
      setCurrentMonster(null);
      setCombatLog([]);
      setMessage('Victory! Continue exploring.');
    } else {
      // Monster attacks back
      const monsterDamage = Math.floor(Math.random() * currentMonster.stats.maxDamage) + currentMonster.stats.minDamage;
      setPlayerHealth(prev => Math.max(0, prev - monsterDamage));
      newCombatLog.push(`${currentMonster.name} deals ${monsterDamage} damage!`);
      
      // Check if player died
      if (playerHealth - monsterDamage <= 0) {
        setPhase(GAME_PHASES.GAME_OVER);
        setMessage('You have been defeated! You wake up back home, having lost everything...');
        setTimeout(() => {
          onGameEnd({
            outcome: 'died',
            rewards: {}, // No rewards when you die
            floorsReached: currentFloor,
            finalHealth: 0,
            finalEnergy: playerEnergy,
            reason: 'Killed in combat'
          });
        }, 2000);
        return;
      }
    }
    
    setCombatLog(newCombatLog);
  }, [phase, currentMonster, monsterHealth, combatLog, ladderFound, playerHealth, floorGrid, playerPosition]);

  // Descend to next floor
  const descendFloor = useCallback(() => {
    if (!ladderFound) {
      setMessage('You need to find the ladder first!');
      return;
    }
    
    // Add floor inventory to total rewards
    setTotalRewards(prev => {
      const newRewards = { ...prev };
      Object.entries(floorInventory).forEach(([item, quantity]) => {
        newRewards[item] = (newRewards[item] || 0) + quantity;
      });
      return newRewards;
    });
    
    setFloorInventory({});
    startNewFloor(currentFloor + 1);
  }, [ladderFound, floorInventory, currentFloor, startNewFloor]);

  // Exit dungeon
  const exitDungeon = useCallback(() => {
    // Combine all rewards
    const finalRewards = { ...totalRewards };
    Object.entries(floorInventory).forEach(([item, quantity]) => {
      finalRewards[item] = (finalRewards[item] || 0) + quantity;
    });
    
    onGameEnd({
      outcome: 'success',
      rewards: finalRewards,
      tripHaulItems: finalRewards, // Add items to trip haul
      floorsReached: currentFloor,
      finalHealth: playerHealth,
      finalEnergy: playerEnergy
    });
  }, [totalRewards, floorInventory, currentFloor, playerHealth, playerEnergy, onGameEnd]);

  // Handle grid clicks
  const handleGridClick = useCallback((x, y) => {
    if (phase !== GAME_PHASES.EXPLORING) return;
    
    const cell = floorGrid[y][x];
    if (!cell) return;
    
    // Check if the clicked cell is adjacent to the player's position
    const dx = Math.abs(x - playerPosition.x);
    const dy = Math.abs(y - playerPosition.y);
    const isAdjacent = (dx <= 1 && dy <= 1) && (dx + dy > 0); // Adjacent includes diagonals, but not same position
    
    if (!isAdjacent) {
      setMessage('You can only move to adjacent spaces!');
      return;
    }
    
    // Mark as discovered
    const newGrid = [...floorGrid];
    newGrid[y][x] = { ...cell, discovered: true };
    setFloorGrid(newGrid);
    
    if (cell.type === 'rock') {
      mineRock(x, y);
    } else if (cell.type === 'empty') {
      // Move to empty space
      setPlayerPosition({ x, y });
      setMessage('You move to the empty space.');
    }
  }, [phase, floorGrid, mineRock, playerPosition]);

  // Handle treasure room
  const claimTreasure = useCallback(() => {
    const treasureRewards = {
      'goldCoins': 50 + (currentFloor * 10),
      'gemShards': 2 + Math.floor(currentFloor / 20),
      'enchantedWood': 1 + Math.floor(currentFloor / 30)
    };
    
    setTotalRewards(prev => {
      const newRewards = { ...prev };
      Object.entries(treasureRewards).forEach(([item, quantity]) => {
        newRewards[item] = (newRewards[item] || 0) + quantity;
      });
      return newRewards;
    });
    
    setMessage('Treasure claimed! Continue to the next floor.');
    setTimeout(() => startNewFloor(currentFloor + 1), 2000);
  }, [currentFloor, startNewFloor]);

  // Handle consumable usage
  const handleUseConsumable = useCallback((itemKey) => {
    const itemData = ItemData[itemKey];
    if (!itemData || itemData.type !== 'consumable') {
      setMessage('Invalid consumable item!');
      return;
    }
    
    const quantity = playerData.inventory[itemKey];
    if (!quantity || quantity <= 0) {
      setMessage(`No ${itemData.name} available!`);
      return;
    }
    
    // Apply effects
    let effectsApplied = false;
    let effectMessages = [];
    
    if (itemData.effects) {
      for (const [effect, value] of Object.entries(itemData.effects)) {
        switch (effect) {
          case 'health':
            const newHealth = Math.min(gameSettings.maxHealth, playerHealth + value);
            if (newHealth > playerHealth) {
              setPlayerHealth(newHealth);
              effectMessages.push(`+${value} Health`);
              effectsApplied = true;
            }
            break;
          case 'energy':
            const newEnergy = Math.min(gameSettings.maxEnergy, playerEnergy + value);
            if (newEnergy > playerEnergy) {
              setPlayerEnergy(newEnergy);
              effectMessages.push(`+${value} Energy`);
              effectsApplied = true;
            }
            break;
          case 'mana':
            // Mana affects real-world stats, not dungeon stats
            effectMessages.push(`+${value} Mana (applied to real stats)`);
            effectsApplied = true;
            break;
          case 'teleport':
            if (value === 'home') {
              exitDungeon();
              effectMessages.push('Teleported home!');
              effectsApplied = true;
            }
            break;
        }
      }
    }
    
    if (effectsApplied) {
      // Consume the item from player's inventory
      const newInventory = { ...playerData.inventory };
      newInventory[itemKey] = quantity - 1;
      if (newInventory[itemKey] <= 0) {
        delete newInventory[itemKey];
      }
      
      // Update player data
      const updatedPlayerData = { ...playerData, inventory: newInventory };
      
      // Apply mana effect to real player data
      if (itemData.effects.mana) {
        const newMana = Math.min(updatedPlayerData.mana.max, updatedPlayerData.mana.current + itemData.effects.mana);
        updatedPlayerData.mana = { ...updatedPlayerData.mana, current: newMana };
      }
      
             // Update the parent component's player data without ending the game
       if (onUpdatePlayerData) {
         onUpdatePlayerData(updatedPlayerData);
       }
      
      setMessage(`Used ${itemData.name}. ${effectMessages.join(', ')}.`);
    } else {
      setMessage(`${itemData.name} had no effect.`);
    }
  }, [playerData, playerHealth, playerEnergy, gameSettings, onGameEnd, onUpdatePlayerData, exitDungeon]);

  // Initialize game on mount - only once, not when playerData changes
  useEffect(() => {
    initializeGame();
  }, []); // Empty dependency array to prevent re-initialization when playerData changes

  // Render grid cell
  const renderGridCell = (cell, x, y) => {
    const isDiscovered = cell.discovered;
    const isPlayerPosition = playerPosition.x === x && playerPosition.y === y;
    const isLadderPosition = ladderPosition && ladderPosition.x === x && ladderPosition.y === y;
    const dx = Math.abs(x - playerPosition.x);
    const dy = Math.abs(y - playerPosition.y);
    const isAdjacent = (dx <= 1 && dy <= 1) && (dx + dy > 0);
    
    let cellClass = styles.gridCell;
    let cellContent = '';
    
    // Ladder position indicator (highest priority)
    if (isLadderPosition && ladderFound) {
      cellClass += ` ${styles.ladderPosition}`;
      cellContent = '🪜';
    }
    // Player position indicator
    else if (isPlayerPosition) {
      cellClass += ` ${styles.playerPosition}`;
      cellContent = '🧙‍♂️';
    } else if (isAdjacent && phase === GAME_PHASES.EXPLORING) {
      cellClass += ` ${styles.adjacentCell}`;
    }
    
    // Show stone icons for undiscovered rocks so players can see where items are
    if (!isLadderPosition && !isPlayerPosition) {
      if (!isDiscovered && cell.type === 'rock') {
        cellClass += ` ${styles.undiscoveredRock}`;
        cellContent = '🪨'; // Show stone icon even when undiscovered
      } else if (!isDiscovered) {
        cellClass += ` ${styles.undiscovered}`;
      } else {
        switch (cell.type) {
          case 'empty':
            cellClass += ` ${styles.empty}`;
            break;
          case 'rock':
            cellClass += ` ${styles.rock}`;
            cellContent = '🪨';
            break;
          default:
            cellClass += ` ${styles.empty}`;
        }
      }
    }
    
    return (
      <div
        key={`${x}-${y}`}
        className={cellClass}
        onClick={() => handleGridClick(x, y)}
        style={{ backgroundColor: isDiscovered ? currentTheme.bgColor : '#333' }}
      >
        {cellContent}
      </div>
    );
  };

  // Render different game phases
  const renderPhaseContent = () => {
    switch (phase) {
      case GAME_PHASES.FLOOR_INTRO:
        return (
          <div className={styles.phaseContent}>
            <h2>Floor {currentFloor}</h2>
            <p className={styles.themeText}>{currentTheme.name}</p>
            <p>{message}</p>
          </div>
        );
      
      case GAME_PHASES.EXPLORING:
        return (
          <div className={styles.gameArea}>
            <div className={styles.gameGrid}>
              {floorGrid.map((row, y) => 
                row.map((cell, x) => renderGridCell(cell, x, y))
              )}
            </div>
            <div className={styles.gameControls}>
              <button 
                className={styles.ladderButton}
                onClick={descendFloor}
                disabled={!ladderFound}
              >
                {ladderFound ? 'Descend (Ladder Found!)' : 'Find the Ladder'}
              </button>
              <button 
                className={styles.exitButton}
                onClick={exitDungeon}
              >
                Exit Dungeon
              </button>
            </div>
          </div>
        );
      
      case GAME_PHASES.COMBAT:
        return (
          <div className={styles.combatArea}>
            <h3>Combat with {currentMonster?.name}</h3>
            <div className={styles.combatDisplay}>
              <div className={styles.playerDisplay}>
                <div className={styles.combatant}>
                  <div className={styles.playerIconContainer}>
                    <div className={styles.playerIcon}>🧙‍♂️</div>
                  </div>
                  <div className={styles.playerHealth}>
                    <div className={styles.healthBarContainer}>
                      <div 
                        className={styles.healthBar}
                        style={{ 
                          width: `${(playerHealth / gameSettings.maxHealth) * 100}%` 
                        }}
                      />
                    </div>
                    <span>Your HP: {playerHealth}/{gameSettings.maxHealth}</span>
                  </div>
                </div>
              </div>
              <div className={styles.combatVs}>VS</div>
              <div className={styles.monsterDisplay}>
                <div className={styles.combatant}>
                  <img 
                    src={`/img/monsters/${currentMonster?.img || 'placeholder.png'}`} 
                    alt={currentMonster?.name}
                    className={styles.monsterImage}
                  />
                  <div className={styles.monsterHealth}>
                    <div className={styles.healthBarContainer}>
                      <div 
                        className={styles.healthBar}
                        style={{ 
                          width: `${(monsterHealth / (currentMonster?.stats.health || 1)) * 100}%` 
                        }}
                      />
                    </div>
                    <span>{currentMonster?.name}: {monsterHealth}/{currentMonster?.stats.health}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.combatLog}>
              {combatLog.map((log, index) => (
                <p key={index}>{log}</p>
              ))}
            </div>
            <button 
              className={styles.attackButton}
              onClick={attackMonster}
            >
              Attack!
            </button>
          </div>
        );
      
      case GAME_PHASES.TREASURE_ROOM:
        return (
          <div className={styles.treasureRoom}>
            <h2>🏆 Treasure Floor {currentFloor}!</h2>
            <p>A room filled with riches awaits!</p>
            <button 
              className={styles.claimTreasureButton}
              onClick={claimTreasure}
            >
              Claim Treasure
            </button>
          </div>
        );
      
      case GAME_PHASES.GAME_OVER:
        return (
          <div className={styles.gameOver}>
            <h2>Game Over</h2>
            <p>{message}</p>
            <button 
              className={styles.exitButton}
              onClick={exitDungeon}
            >
              Return to Surface
            </button>
          </div>
        );
      
      default:
        return (
          <div className={styles.phaseContent}>
            <p>Loading...</p>
          </div>
        );
    }
  };

  return (
    <div className={styles.dungeonMiningMinigame}>
      <div className={styles.leftSidebar}>
        <div className={styles.sidebarHeader}>
          <h1>⛏️ Dungeon Mining</h1>
        </div>
        
        <div className={styles.statsPanel}>
          <div className={styles.statGroup}>
            <h3>Player Status</h3>
            <div className={styles.floorInfo}>Floor: {currentFloor}</div>
            
            <div className={styles.statusDisplayContainer}>
              <div className={styles.statusItem}>
                <span className={styles.statusEmoji}>❤️</span>
                <div className={styles.statusBarContainer}>
                  <div className={styles.statusBarBackground}>
                    <div
                      className={styles.statusBarFill}
                      style={{ 
                        width: `${(playerHealth / gameSettings.maxHealth) * 100}%`, 
                        backgroundColor: '#e74c3c' 
                      }}
                    />
                  </div>
                  <span className={styles.statusText}>{playerHealth}/{gameSettings.maxHealth}</span>
                </div>
              </div>
              
              <div className={styles.statusItem}>
                <span className={styles.statusEmoji}>💧</span>
                <div className={styles.statusBarContainer}>
                  <div className={styles.statusBarBackground}>
                    <div
                      className={styles.statusBarFill}
                      style={{ 
                        width: `${(playerData.mana.current / playerData.mana.max) * 100}%`, 
                        backgroundColor: '#3498db' 
                      }}
                    />
                  </div>
                  <span className={styles.statusText}>{playerData.mana.current}/{playerData.mana.max}</span>
                </div>
              </div>
              
              <div className={styles.statusItem}>
                <span className={styles.statusEmoji}>⚡</span>
                <div className={styles.statusBarContainer}>
                  <div className={styles.statusBarBackground}>
                    <div
                      className={styles.statusBarFill}
                      style={{ 
                        width: `${(playerEnergy / gameSettings.maxEnergy) * 100}%`, 
                        backgroundColor: '#2ecc71' 
                      }}
                    />
                  </div>
                  <span className={styles.statusText}>{playerEnergy}/{gameSettings.maxEnergy}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.inventorySection}>
          <div className={styles.inventory}>
            <h3>Trip Haul</h3>
            <div className={styles.tripHaulGrid}>
              {Object.entries(floorInventory).map(([item, quantity]) => (
                <div key={item} className={styles.tripHaulSlot}>
                  <div className={styles.tripHaulIcon}>
                    <img src={`/img/${ItemData[item]?.img || 'placeholder.png'}`} alt={item} />
                    <div className={styles.tripHaulQuantity}>{quantity}</div>
                  </div>
                </div>
              ))}
              {/* Show empty slots up to 10 total to avoid scroll */}
              {Array.from({ length: Math.max(0, 10 - Object.keys(floorInventory).length) }).map((_, index) => (
                <div key={`empty-${index}`} className={`${styles.tripHaulSlot} ${styles.emptyTripHaulSlot}`}>
                  <div className={styles.tripHaulIcon}></div>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.consumables}>
            <h3>Consumables</h3>
            <div className={styles.consumableItems}>
              {Object.entries(playerData.inventory).filter(([itemKey, quantity]) => {
                const itemData = ItemData[itemKey];
                return itemData && itemData.type === 'consumable' && quantity > 0;
              }).map(([itemKey, quantity]) => (
                <div key={itemKey} className={styles.consumableItem}>
                  <img src={`/img/${ItemData[itemKey]?.img || 'placeholder.png'}`} alt={itemKey} />
                  <span>{ItemData[itemKey]?.name || itemKey}: {quantity}</span>
                  <button 
                    className={styles.useButton}
                    onClick={() => handleUseConsumable(itemKey)}
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.mainContent}>
        <div className={styles.gameArea}>
          {renderPhaseContent()}
        </div>
        
        <div className={styles.message}>
          {message}
        </div>
      </div>
    </div>
  );
};

export default DungeonMiningMinigame; 